import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, input, output, signal, ViewChild, WritableSignal } from '@angular/core'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { ComponentWithDataSetter } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { EventTimePickerDropdownPanelComponent } from '../event-time-picker-dropdown-panel/event-time-picker-dropdown-panel.component'
import { NgClass } from '@angular/common'

/** 24h strings `00`–`23` */
export const EVENT_TIME_PICKER_HOUR_OPTIONS: readonly string[] = Array.from({ length: 24 }, (_: unknown, i: number) => String(i).padStart(2, '0'))

/** Matches main event timings + time-range picker + scheduler drag (15-minute grid; :59 for end-of-hour). */
export const EVENT_TIME_PICKER_MINUTE_OPTIONS = ['00', '15', '30', '45', '59'] as const

/**
 * @description Snap 0–59 to nearest {@link EVENT_TIME_PICKER_MINUTE_OPTIONS} value.
 * Used by legacy pickers that expect `:59` as an end-of-hour option.
 * @param minute - Minute number (0–59).
 * @returns Snapped minute token.
 */
export function snapMinuteToEventPickerGrid(minute: number): string {
  const roundedMinute = Math.min(59, Math.max(0, Math.round(minute)))
  if (roundedMinute >= 52) return '59'
  if (roundedMinute >= 37) return '45'
  if (roundedMinute >= 22) return '30'
  if (roundedMinute >= 7) return '15'
  return '00'
}

let _eventTimePickerSeq: number = 0

/**
 * @description Create monotonic id for DOM attributes so multiple instances never collide.
 * @returns Unique numeric id for current runtime session.
 */
function nextEventTimePickerId(): number {
  return ++_eventTimePickerSeq
}

@Component({
  selector: 'app-event-time-picker',
  standalone: true,
  imports: [DropdownComponent, NgClass],
  templateUrl: './event-time-picker.component.html',
  styleUrl: './event-time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventTimePickerComponent {
  @ViewChild('timeDropdown') private _timeDropdown?: DropdownComponent
  @ViewChild('hourInput') private _hourInput?: ElementRef<HTMLInputElement>
  @ViewChild('minuteInput') private _minuteInput?: ElementRef<HTMLInputElement>

  /** When true, ignore parent `hour` sync so typing is not overwritten mid-edit. */
  private _hourFocused: boolean = false
  /** When true, ignore parent `minute` sync so typing is not overwritten mid-edit. */
  private _minuteFocused: boolean = false

  /**
   * @description Optional field label shown above the picker.
   */
  public readonly label = input<string>('')
  /**
   * @description Two-digit hour `00`–`23`.
   */
  public readonly hour = input<string>('00')
  /**
   * @description Two-digit minute token; should exist in `minuteOptions`.
   */
  public readonly minute = input<string>('00')
  /**
   * @description When `true`, shows error border.
   */
  public readonly invalid = input<boolean>(false)
  /**
   * @description List of hours shown in panel.
   */
  public readonly hourOptions = input<readonly string[]>(EVENT_TIME_PICKER_HOUR_OPTIONS)
  /**
   * @description List of minute options shown in panel.
   */
  public readonly minuteOptions = input<readonly string[]>(['00', '15', '30', '45'])
  /**
   * @description When `true`, show clock icon inside trigger.
   */
  public readonly showClockIcon = input<boolean>(true)
  /**
   * @description Label size variant used in different UIs.
   */
  public readonly labelTextSize = input<'xs' | 'sm'>('sm')
  /**
   * @description Fixed height variant used in weekly “same hours” row.
   */
  public readonly fixedHeight = input<boolean>(false)
  /**
   * @description Flex behavior to match modal row layout.
   */
  public readonly flexInRow = input<boolean>(false)

  /**
   * @description Emits hour token when user selects new hour.
   */
  public readonly hourChange = output<string>()
  /**
   * @description Emits minute token when user selects new minute.
   */
  public readonly minuteChange = output<string>()

  protected readonly dropdownOpen: WritableSignal<boolean> = signal<boolean>(false)
  /** Segment shown while typing hours (`00`–`23`). */
  protected readonly hourText: WritableSignal<string> = signal<string>('00')
  /** Segment shown while typing minutes (snapped on blur). */
  protected readonly minuteText: WritableSignal<string> = signal<string>('00')
  private readonly _instanceId: number = nextEventTimePickerId()
  protected readonly triggerId: string = `event-time-picker-trigger-${this._instanceId}`
  protected readonly panelId: string = `event-time-picker-panel-${this._instanceId}`
  protected readonly dropdownDomId: string = `event-time-picker-dd-${this._instanceId}`

  constructor() {
    effect(() => {
      const hh: string = this.hour()
      const mm: string = this.minute()
      if (this._hourFocused || this._minuteFocused) return
      this.hourText.set(hh)
      this.minuteText.set(mm)
    })
  }

  /**
   * @description Opens the dropdown panel from the clock icon only.
   * @param event - Click event from the icon.
   */
  public openFromClockIcon(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this._timeDropdown?.openDropdown(event)
  }

  /**
   * @description Prevents the dropdown from toggling when interacting with the time segments.
   * @param event - Click event from the input.
   */
  public onSegmentClick(event: MouseEvent): void {
    event.stopPropagation()
  }

  /**
   * @description Select-all on focus so the next keystroke replaces the segment (overwrite typing).
   * @param event - Focus event from the hour input.
   */
  public onHourFocus(event: FocusEvent): void {
    this._hourFocused = true
    const el = event.target as HTMLInputElement | null
    if (el) {
      queueMicrotask(() => el.select())
    }
  }

  /**
   * @description Digits only for the hour segment (max 2). After two digits, moves focus to minutes with the value selected so typing overwrites it.
   * @param value - Raw input value.
   */
  public onHourInput(value: string): void {
    const v = (value ?? '').replace(/\D/g, '').slice(0, 2)
    this.hourText.set(v)
    if (v.length === 2) {
      queueMicrotask(() => this._focusMinuteAndSelect())
    }
  }

  /**
   * @description Clamp hour to `00`–`23` and emit when changed.
   */
  public onHourBlur(): void {
    this._hourFocused = false
    const raw = this.hourText().trim()
    if (!raw) {
      this.hourText.set(this.hour())
      return
    }
    let h = Number.parseInt(raw, 10)
    if (Number.isNaN(h)) {
      this.hourText.set(this.hour())
      return
    }
    h = Math.min(23, Math.max(0, h))
    const hh = String(h).padStart(2, '0')
    this.hourText.set(hh)
    if (hh !== this.hour()) {
      this.hourChange.emit(hh)
    }
  }

  /**
   * @description Move focus to the minutes segment.
   * @param event - Keydown on the hour field.
   */
  public onHourKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== ':') return
    event.preventDefault()
    this._focusMinuteAndSelect()
  }

  /**
   * @description Focus minutes and select its text so the next keystroke replaces the segment.
   */
  private _focusMinuteAndSelect(): void {
    const el = this._minuteInput?.nativeElement
    if (!el) return
    el.focus()
    queueMicrotask(() => el.select())
  }

  /**
   * @description Select-all on focus so the next keystroke replaces the segment.
   * @param event - Focus event from the minute input.
   */
  public onMinuteFocus(event: FocusEvent): void {
    this._minuteFocused = true
    const el = event.target as HTMLInputElement | null
    if (el) {
      queueMicrotask(() => el.select())
    }
  }

  /**
   * @description Digits only for the minute segment (max 2).
   * @param value - Raw input value.
   */
  public onMinuteInput(value: string): void {
    const v = (value ?? '').replace(/\D/g, '').slice(0, 2)
    this.minuteText.set(v)
  }

  /**
   * @description Snap minutes to the picker grid; may adjust hour near end-of-day (`23:59`).
   */
  public onMinuteBlur(): void {
    this._minuteFocused = false
    const raw = this.minuteText().trim()
    if (!raw) {
      this.minuteText.set(this.minute())
      return
    }
    let m = Number.parseInt(raw, 10)
    if (Number.isNaN(m)) {
      this.minuteText.set(this.minute())
      return
    }
    m = Math.min(59, Math.max(0, m))
    let h = Number.parseInt(this.hour(), 10)
    if (Number.isNaN(h)) h = 0
    h = Math.min(23, Math.max(0, h))
    const rounded = this._snapToNearestPickerQuarter(h, m)
    const hh = String(rounded.h).padStart(2, '0')
    const mm = String(rounded.m).padStart(2, '0')
    this.hourText.set(hh)
    this.minuteText.set(mm)
    if (hh !== this.hour()) {
      this.hourChange.emit(hh)
    }
    if (mm !== this.minute()) {
      this.minuteChange.emit(mm)
    }
  }

  /**
   * @description Move focus to the hours segment.
   * @param event - Keydown on the minute field.
   */
  public onMinuteKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft') return
    event.preventDefault()
    const el = this._hourInput?.nativeElement
    if (!el) return
    el.focus()
    queueMicrotask(() => el.select())
  }

  /**
   * @description Snap minute to the nearest 15-minute boundary (`Math.round`), rolling hour when needed.
   * Special-case end-of-day: `23:52+` becomes `23:59`.
   * @param hour - Hour (0–23).
   * @param minute - Minute (0–59).
   * @returns Snapped time parts.
   */
  private _snapToNearestPickerQuarter(hour: number, minute: number): { h: number; m: number } {
    if (hour === 23 && minute >= 52) return { h: 23, m: 59 }
    const rounded = Math.round(minute / 15) * 15
    if (rounded === 60) {
      const nextHour = Math.min(23, hour + 1)
      return { h: nextHour, m: 0 }
    }
    return { h: hour, m: rounded }
  }

  /**
   * @description Dropdown panel config for shared dropdown component.
   * @returns Panel config that wires options, selection, and selection handlers.
   */
  protected readonly panelConfig = computed<ComponentWithDataSetter<EventTimePickerDropdownPanelComponent>>(() => {
    const hourOptions: readonly string[] = this.hourOptions()
    const minuteOptions: readonly string[] = this.minuteOptions()
    const selectedHour: string = this.hour()
    const selectedMinute: string = this.minute()

    return {
      component: EventTimePickerDropdownPanelComponent,
      dataSetter: (inst: EventTimePickerDropdownPanelComponent): void => {
        inst.panelId.set(this.panelId)
        inst.hourOptions.set(hourOptions)
        inst.minuteOptions.set(minuteOptions)
        inst.selectedHour.set(selectedHour)
        inst.selectedMinute.set(selectedMinute)
        inst.onHourSelect = (v: string) => this.hourChange.emit(v)
        inst.onMinuteSelect = (v: string) => this.minuteChange.emit(v)
        inst.onEscapeClose = () => this._timeDropdown?.close()
      }
    }
  })
}
